import * as React from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import Widget from '../Widgets/Widget';
import { CardItem, GridSizeValue } from './types';
import { IWidget } from '../Widgets/types';
import { getWidgetsByLayout } from '../Widgets/WidgetService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IPreviewModalButton {
  id: number;
  title: string;
  callback: () => void;
  tooltip?: string;
}

interface IPreviewModalProps {
  title: string;
  id: string;
  cards: CardItem[];
  activeTab: number;
  gridSize: GridSizeValue;
  widgets: IWidget[];
  closeCallback: () => void;
  closeTooltip?: string;
}

export default function PreviewModal(props: IPreviewModalProps): JSX.Element {
  const { trackEvent } = useAnalytics();

  const handleEscapeKey = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        trackEvent(['Preview card modal', 'keypress', `Close modal`]);
        props.closeCallback();
      }
    },
    [props]
  );

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    trackEvent(['Preview card modal', 'click', `Close modal`]);
    props.closeCallback();
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleEscapeKey]);

  const getStaticWidgets = (widgets: Layout[]) => {
    if (widgets) {
      const newWidgets = widgets.map((widgetItem: Layout) => ({
        ...widgetItem,
        static: true,
      }));

      return newWidgets;
    }
    return [];
  };

  return (
    <div className="modal-wrapper modal-wrapper--xl">
      <div className="modal-wrapper__content">
        <div className="modal" id={props.id}>
          <div className="modal__header">
            <div className="row">
              <div className="col">
                <h4 className="modal__header__title">{props.title}</h4>
              </div>
              <div className="col-auto">
                <button
                  style={{ background: 'none', border: 'none' }}
                  title={props.closeTooltip || ''}
                  className="cmd-icon cmd-icon-remove close-modal"
                  onClick={handleClose}
                />
              </div>
            </div>
          </div>

          <div className="modal__content">
            <div
              className="w-map online-panel-preview-bg"
              style={{
                position: 'relative',
                left: 0,
                minHeight: '500px',
                // background: `url('/assets/map-bg.jpg') top left`,
              }}
            >
              <div className="b-widgets-info">
                <div className="b-tabs-toggler">
                  <ul>
                    {props.cards.map((card: CardItem, index: number) => (
                      <li
                        key={`card-${index}`}
                        className={`${props.activeTab === index ? 'active' : ''}`}
                      >
                        <a href="#" title={card.name}>
                          {card.name || <span>&nbsp;</span>}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="b-widgets-info-tab-content">
                  <GridLayout
                    className="b-widget-grid b-widget-grid--rounded"
                    key={`gridlayout-${props.cards[props.activeTab].name}`}
                    style={{
                      gridTemplateColumns: `repeat(${props.gridSize.columns}, min-content)`,
                      gridTemplateRows: `repeat(${props.gridSize.rows}, min-content)`,
                      minWidth: props.gridSize.columns * 72 + (props.gridSize.columns - 1) * 4,
                      minHeight: props.gridSize.rows * 72 + (props.gridSize.rows - 1) * 4,
                    }}
                    layout={getStaticWidgets(props.cards[props.activeTab].widgets)}
                    cols={props.gridSize.columns}
                    maxRows={props.gridSize.rows}
                    rowHeight={72}
                    width={props.gridSize.columns * 72 + (props.gridSize.columns - 1) * 4}
                    isResizable={false}
                    isDroppable={false}
                    margin={[4, 4]}
                  >
                    {getWidgetsByLayout(
                      props.widgets,
                      getStaticWidgets(props.cards[props.activeTab].widgets)
                    ).map((widget: IWidget, widgetIndex: number) => (
                      <div className="item" key={`${widget.gridName}`}>
                        <Widget
                          key={`${widgetIndex}-${widget.gridName}`}
                          index={widgetIndex}
                          data={widget}
                          isMockupMode={true}
                          isInGrid={false}
                        />
                      </div>
                    ))}
                  </GridLayout>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
